import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { EntityPickerOptions } from '@adornis/entity-picker/client/entity-picker-options.js';
import { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import type { AspectConfig } from '@adornis/filter/filter-options.js';
import { PartnerAPI } from '@chancenwerk/master-data-management/partner/api/PartnerAPI.js';
import { PartnerFilter } from '@chancenwerk/master-data-management/partner/api/subscribePartnerByFilter.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { html } from 'lit';

@Entity()
export class PartnerAspect extends AbstractAspect {
  static override _class = 'PartnerAspect';

  constructor(doc: PartialEntityData<PartnerAspect>) {
    super(doc);
  }

  @Field(type => [ID]) value: Maybe<string[]>;
  @Field(type => [String]) name: Maybe<string[]>;

  override getMongoFilter(): object | null {
    if (!this.fieldName || !this.value?.length) return null;

    return {
      [this.fieldName]: { $in: this.value },
    };
  }
}

export const PartnerAspectSchema: (filter?: PartnerFilter) => AspectConfig<PartnerAspect> = (
  filter: PartnerFilter = new PartnerFilter({}),
) => ({
  aspectConstructor: PartnerAspect,
  renderEditor: ({ aspectValue }) => html`
    <x-multi-entity-picker
      placeholder-mode="hide"
      placeholder="Partner"
      clearable
      .options=${{
        class: Partner,
        operation: PartnerAPI.subscribeByFilter,
        selectionSet: { name: 1, kuerzel: 1 },
        filter: filter ?? new PartnerFilter({}),
      } satisfies EntityPickerOptions<typeof Partner>}
      .renderString=${(e: Maybe<Partner>) => (!!e ? `${e.kuerzel ?? 'Kein Kürzel angegeben'}` : '')}
      .renderItem=${(e: Maybe<Partner>, index: number) => {
        const text = !!e ? `${e.kuerzel ?? 'Kein Kürzel angegeben'}${e.name ? ` / ${e.name} ` : ''}` : '';
        return html`<cr-flex
          ${css({
            cursor: 'pointer',
          })}
          space="sm"
          padding="md"
        >
          <x-checkbox disabled boolean-only .value=${aspectValue.value?.includes(e._id)} label=${text}></x-checkbox>
        </cr-flex> `;
      }}
      @value-picked=${(e: ValueEvent<Maybe<string[]>>) => {
        aspectValue.value = e.detail.value ?? [];
      }}
      @entity-picked=${(e: ValueEvent<Maybe<Partner[]>>) => {
        aspectValue.name = e.detail.value?.map(p => p.toString() ?? '') ?? [];
      }}
    ></x-multi-entity-picker>
  `,
  renderPreview: ({ aspectValue }) => (aspectValue.name ? html` ${aspectValue.name} ` : null),
});
